import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Edit2, Trash2, User } from 'lucide-react';
import { TicketsService } from '@/services';
import { ComentarioInterno } from '@/types';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import Modal from '../ui/Modal';
import ConfirmationModal from '@/components/ConfirmationModal';

interface InternalCommentsProps {
  ticketId: number;
  onShowModal?: () => void;
}

export default function InternalComments({ ticketId, onShowModal }: InternalCommentsProps) {
  const { usuario } = useAuth();
  const [comentarios, setComentarios] = useState<ComentarioInterno[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [novoComentario, setNovoComentario] = useState('');
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [conteudoEditando, setConteudoEditando] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [comentarioToDelete, setComentarioToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (usuario) {
      loadComentarios();
    }
  }, [ticketId, usuario]);

  const loadComentarios = async () => {
    try {
      const data = await TicketsService.getComentariosInternos(ticketId);
      setComentarios(data);
    } catch (error: any) {
      console.error('Erro ao carregar comentários internos:', error);
    }
  };

  const handleAdicionar = async () => {
    if (!novoComentario.trim()) {
      toast.error('O comentário não pode estar vazio');
      return;
    }

    try {
      setIsLoading(true);
      await TicketsService.adicionarComentarioInterno(ticketId, novoComentario);
      await loadComentarios();
      setNovoComentario('');
      setShowForm(false);
      toast.success('Comentário adicionado com sucesso');
    } catch (error: any) {
      console.error('Erro ao adicionar comentário:', error);
      toast.error(error.response?.data?.error || 'Erro ao adicionar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditar = async (comentarioId: number) => {
    if (!conteudoEditando.trim()) {
      toast.error('O comentário não pode estar vazio');
      return;
    }

    try {
      setIsLoading(true);
      await TicketsService.atualizarComentarioInterno(ticketId, comentarioId, conteudoEditando);
      await loadComentarios();
      setEditandoId(null);
      setConteudoEditando('');
      toast.success('Comentário atualizado com sucesso');
    } catch (error: any) {
      console.error('Erro ao atualizar comentário:', error);
      toast.error(error.response?.data?.error || 'Erro ao atualizar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletarClick = (comentarioId: number) => {
    setComentarioToDelete(comentarioId);
    setShowConfirmModal(true);
  };

  const handleDeletar = async () => {
    if (!comentarioToDelete) return;

    try {
      setIsLoading(true);
      await TicketsService.deletarComentarioInterno(ticketId, comentarioToDelete);
      await loadComentarios();
      toast.success('Comentário excluído com sucesso');
      setShowConfirmModal(false);
      setComentarioToDelete(null);
    } catch (error: any) {
      console.error('Erro ao deletar comentário:', error);
      toast.error(error.response?.data?.error || 'Erro ao deletar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const podeEditar = (comentario: ComentarioInterno) => {
    return (
      usuario &&
      (comentario.usuario_id === usuario.id || usuario.nivel === 'admin')
    );
  };

  if (!usuario) {
    return null;
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Comentários Internos
            </h3>
          </div>
          <div className="flex items-center gap-1.5">
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title="Adicionar comentário interno"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
            {comentarios.length > 0 && (
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                title="Ver todos os comentários"
              >
                <MessageSquare className="w-4 h-4" />
                <span>{comentarios.length}</span>
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <div className="w-full p-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 space-y-3">
            <textarea
              value={novoComentario}
              onChange={(e) => setNovoComentario(e.target.value)}
              placeholder="Adicione um comentário interno..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              disabled={isLoading}
            />
            <div className="flex gap-2">
              <button
                onClick={handleAdicionar}
                disabled={!novoComentario.trim() || isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {isLoading ? 'Adicionando...' : 'Adicionar'}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setNovoComentario('');
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {comentarios.length === 0 && !showForm && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nenhum comentário interno
          </p>
        )}
        {comentarios.length > 0 && !showForm && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {comentarios.length} {comentarios.length === 1 ? 'comentário' : 'comentários'}
          </p>
        )}
      </div>

      {/* Modal de Comentários Internos */}
      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  Comentários Internos
                </div>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  {comentarios.length}{" "}
                  {comentarios.length === 1 ? "comentário" : "comentários"}
                </p>
              </div>
            </div>
          }
          maxWidth="xl"
        >
            {/* Conteúdo do Modal */}
            <div className="p-4 sm:p-5 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-3">
                {comentarios.map((comentario) => (
                  <div
                    key={comentario.id}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {editandoId === comentario.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={conteudoEditando}
                          onChange={(e) => setConteudoEditando(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                          disabled={isLoading}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditar(comentario.id)}
                            disabled={!conteudoEditando.trim() || isLoading}
                            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                          >
                            Salvar
                          </button>
                          <button
                            onClick={() => {
                              setEditandoId(null);
                              setConteudoEditando('');
                            }}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-blue-100 dark:from-primary-900/50 dark:to-blue-900/50 rounded-full flex items-center justify-center shadow-md border-2 border-primary-200/50 dark:border-primary-700/50 overflow-hidden">
                            {comentario.usuario_avatar ? (
                              <img
                                src={`${import.meta.env.VITE_API_URL}/img/avatars/${comentario.usuario_avatar}`}
                                alt={`Avatar de ${comentario.usuario_nome}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-primary-700 dark:text-primary-300 font-bold text-sm">
                                {comentario.usuario_nome?.charAt(0) || "U"}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Conteúdo */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                              {comentario.usuario_nome}
                            </span>
                            {comentario.created_at && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(comentario.created_at)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                            {comentario.comentario}
                          </p>
                        </div>

                        {/* Ações */}
                        {podeEditar(comentario) && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => {
                                setEditandoId(comentario.id);
                                setConteudoEditando(comentario.comentario);
                              }}
                              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                            <button
                              onClick={() => handleDeletarClick(comentario.id)}
                              disabled={isLoading}
                              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
        </Modal>
      )}

      {/* Modal de Confirmação de Excluir Comentário */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          if (!isLoading) {
            setShowConfirmModal(false);
            setComentarioToDelete(null);
          }
        }}
        onConfirm={handleDeletar}
        title="Excluir Comentário"
        message="Deseja realmente excluir este comentário?"
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isLoading}
      />
    </>
  );
}

